import React, { useState, useEffect } from 'react';
import { Building2, Search, Wrench, Mail, CreditCard } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { staffTabClass, staffInputClass } from '../components/layout/staffPageStyles';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CompaniesTab } from '../components/b2b/CompaniesTab';
import { RepairsTab } from '../components/b2b/RepairsTab';
import { PaymentsTab } from '../components/b2b/PaymentsTab';
import { EditRepairModal } from '../components/b2b/EditRepairModal';
import { EditCompanyModal } from '../components/b2b/EditCompanyModal';
import ChatModal from '../components/ChatModal';
import B2BRepairDetailModal from '../components/b2b/B2BRepairDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminB2BPage = () => {
  const { api } = useAuth();
  const { t, tError } = useLanguage();
  const [activeTab, setActiveTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [repairStatusFilter, setRepairStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusModalRepair, setStatusModalRepair] = useState(null);
  const [priceModalRepair, setPriceModalRepair] = useState(null);
  const [priceForm, setPriceForm] = useState({ price_repair: 0, price_diagnostic: 0 });
  const [recapLoading, setRecapLoading] = useState(false);
  const [recapResult, setRecapResult] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [repairInvoices, setRepairInvoices] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentTotals, setPaymentTotals] = useState({ total_amount: 0, by_method: {} });
  const [payFilterCompany, setPayFilterCompany] = useState('');
  const [payFilterDateDebut, setPayFilterDateDebut] = useState('');
  const [payFilterDateFin, setPayFilterDateFin] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [companyEditForm, setCompanyEditForm] = useState({});
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);
  const [sendingEmailRef, setSendingEmailRef] = useState(null);
  const [editRepair, setEditRepair] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const [detailRepair, setDetailRepair] = useState(null);

  // ─── Data fetchers ──────────────────────────────────────────────
  const fetchCompanies = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/admin/b2b/companies${params}`);
      setCompanies(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchRepairs = async () => {
    try {
      const params = repairStatusFilter ? `?status=${repairStatusFilter}` : '';
      const res = await api.get(`/admin/b2b/repairs${params}`);
      setRepairs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPaymentHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (payFilterCompany) params.append('company_id', payFilterCompany);
      if (payFilterDateDebut) params.append('date_debut', payFilterDateDebut);
      if (payFilterDateFin) params.append('date_fin', payFilterDateFin);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/admin/b2b/payments/history${qs}`);
      setPaymentHistory(res.data.payments || []);
      setPaymentTotals({ total_amount: res.data.total_amount || 0, by_method: res.data.by_method || {} });
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    const load = async () => { setLoading(true); await Promise.all([fetchCompanies(), fetchRepairs()]); setLoading(false); };
    load();
  }, []);

  useEffect(() => { fetchCompanies(); }, [statusFilter]);
  useEffect(() => { fetchRepairs(); }, [repairStatusFilter]);
  useEffect(() => { if (activeTab === 'payments') fetchPaymentHistory(); }, [activeTab]);

  // ─── Company handlers ───────────────────────────────────────────
  const updateCompanyStatus = async (companyId, newStatus) => {
    const labels = { valide: 'valider', refuse: 'refuser', suspendu: 'suspendre', en_attente: 'remettre en attente' };
    if (!window.confirm(`Voulez-vous ${labels[newStatus]} cette entreprise ?`)) return;
    try { await api.put(`/admin/b2b/companies/${companyId}/status?new_status=${newStatus}`); fetchCompanies(); }
    catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const openCompanyEditModal = (company) => {
    setCompanyEditForm({
      company_name: company.company_name || '', manager_name: company.manager_name || '',
      email: company.email || '', phone: company.phone || '',
      siret: company.siret || '', address: company.address || '', new_password: '',
    });
    setEditModal(company);
  };

  const submitEditCompany = async () => {
    if (!editModal) return;
    setSavingCompanyEdit(true);
    try {
      const payload = { ...companyEditForm };
      if (!payload.new_password) delete payload.new_password;
      await api.put(`/admin/b2b/companies/${editModal.id}/edit`, payload);
      setEditModal(null);
      fetchCompanies();
    } catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur lors de la modification'); }
    finally { setSavingCompanyEdit(false); }
  };

  const deleteCompany = async (company) => {
    if (!window.confirm(`ATTENTION : Supprimer definitivement l'entreprise "${company.company_name}" et toutes ses donnees ?`)) return;
    try { await api.delete(`/admin/b2b/companies/${company.id}`); fetchCompanies(); }
    catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur lors de la suppression'); }
  };

  // ─── Repair handlers ───────────────────────────────────────────
  const updateRepairStatus = async (ref, newStatus) => {
    if (!window.confirm(`Changer le statut de ${ref} en "${newStatus}" ?`)) return;
    try { await api.put(`/admin/b2b/repairs/${ref}/status?new_status=${newStatus}`); fetchRepairs(); setStatusModalRepair(null); }
    catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const confirmArrival = async (ref) => {
    if (!window.confirm(`Confirmer la réception de ${ref} en boutique ?`)) return;
    try { await api.put(`/admin/b2b/repairs/${ref}/confirm-arrival`); fetchRepairs(); }
    catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const acceptRefuseRepair = async (ref, action) => {
    const label = action === 'accept' ? 'accepter' : 'refuser';
    if (!window.confirm(`Voulez-vous ${label} la réparation ${ref} ?`)) return;
    try { await api.put(`/admin/b2b/repairs/${ref}/accept?action=${action}`); fetchRepairs(); }
    catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const deleteRepair = async (ref) => {
    if (!window.confirm(`Supprimer définitivement la réparation ${ref} et ses factures associées ?`)) return;
    try { await api.delete(`/admin/b2b/repairs/${ref}`); fetchRepairs(); }
    catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const openEditModal = (repair) => {
    setEditForm({
      brand: repair.brand || '', model: repair.model || '',
      imei_serial: repair.imei_serial || '', problem: repair.problem || '',
      notes: repair.notes || '', lock_type: repair.lock_type || '',
      device_lock: repair.device_lock || '',
      delivery_method: repair.delivery_method || 'boutique',
      tracking_number: repair.tracking_number || '',
      back_glass_broken: repair.back_glass_broken || false,
      customer_provides_part: repair.customer_provides_part || false,
      parts_check: repair.parts_check || {},
      price_repair: repair.price_repair || 0,
      price_diagnostic: repair.price_diagnostic || 0,
    });
    setEditRepair(repair);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try { await api.put(`/admin/b2b/repairs/${editRepair.ref}/edit`, editForm); setEditRepair(null); fetchRepairs(); }
    catch (err) { alert(err?.response?.data?.detail || 'Erreur'); }
    finally { setSavingEdit(false); }
  };

  const sendDocumentEmail = async (ref, docType) => {
    setSendingEmailRef(ref);
    try { const res = await api.post(`/admin/b2b/repairs/${ref}/send-document-email`, { doc_type: docType }); alert(res.data.message || 'Email envoyé'); }
    catch (err) { alert(err?.response?.data?.detail || 'Erreur envoi email'); }
    finally { setSendingEmailRef(null); }
  };

  // ─── Price & Invoice handlers ──────────────────────────────────
  const openPriceModal = async (repair) => {
    setPriceForm({ price_repair: repair.price_repair || 0, price_diagnostic: repair.price_diagnostic || 0 });
    setPriceModalRepair(repair);
    try { const res = await api.get(`/admin/b2b/repairs/${repair.ref}/invoices`); setRepairInvoices(res.data || []); }
    catch { setRepairInvoices([]); }
  };

  const savePrice = async () => {
    if (!priceModalRepair) return;
    try {
      const res = await api.put(`/admin/b2b/repairs/${priceModalRepair.ref}/set-price`, priceForm);
      fetchRepairs(); setPriceModalRepair(null);
      if (res.data.diagnostic_locked) alert('Le prix du diagnostic est verrouillé (facture déjà payée).');
    } catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const markInvoicePaid = async (invoiceId, method) => {
    try {
      await api.put(`/admin/b2b/invoices/${invoiceId}/mark-paid`, { payment_method: method });
      fetchRepairs();
      if (activeTab === 'payments') fetchPaymentHistory();
      if (invoiceModal) { const res = await api.get(`/admin/b2b/repairs/${invoiceModal.ref}/invoices`); setRepairInvoices(res.data || []); }
      if (priceModalRepair) { const res = await api.get(`/admin/b2b/repairs/${priceModalRepair.ref}/invoices`); setRepairInvoices(res.data || []); }
    } catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const finalizeBilling = async (ref) => {
    if (!window.confirm('Finaliser la facturation pour cette réparation ?')) return;
    try {
      const res = await api.post(`/admin/b2b/repairs/${ref}/finalize-invoice`);
      alert(`Facturation finalisée: ${res.data.mode === 'two_invoices' ? 'Deux factures (diagnostic payé + réparation)' : res.data.mode === 'combined' ? 'Facture combinée' : res.data.mode}`);
      fetchRepairs();
      if (invoiceModal) { const invRes = await api.get(`/admin/b2b/repairs/${ref}/invoices`); setRepairInvoices(invRes.data || []); }
    } catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
  };

  const printInvoiceById = async (invoiceId) => {
    try {
      const res = await api.get(`/b2b/invoices/by-id/${invoiceId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'width=900,height=700');
      if (!w) { alert('Veuillez autoriser les popups pour imprimer.'); return; }
      const triggerPrint = () => { w.focus(); w.print(); };
      w.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch { alert('Erreur lors du téléchargement de la facture'); }
  };

  const handlePrintPdf = async (ref) => {
    try {
      const res = await api.get(`/b2b/repairs/${ref}/fiche-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'width=900,height=700');
      if (!w) { alert('Veuillez autoriser les popups pour imprimer.'); return; }
      const triggerPrint = () => { w.focus(); w.print(); };
      w.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch { alert('Erreur lors du téléchargement du PDF'); }
  };

  const handlePrintInvoice = async (ref) => {
    try {
      const res = await api.get(`/b2b/invoices/${ref}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'width=900,height=700');
      if (!w) { alert('Veuillez autoriser les popups pour imprimer.'); return; }
      const triggerPrint = () => { w.focus(); w.print(); };
      w.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch { alert('Erreur lors du téléchargement de la facture'); }
  };

  // ─── Recap ─────────────────────────────────────────────────────
  const sendMonthlyRecap = async () => {
    const month = prompt('Mois (1-12) ? Laissez vide pour le mois précédent :');
    const year = month ? prompt('Année ?') : null;
    setRecapLoading(true); setRecapResult(null);
    try {
      const body = {};
      if (month) body.month = parseInt(month);
      if (year) body.year = parseInt(year);
      const res = await api.post('/admin/b2b/monthly-recap', body);
      setRecapResult(res.data.results);
    } catch (err) { alert(tError(err.response?.data?.detail) || 'Erreur'); }
    setRecapLoading(false);
  };

  const pendingArrivals = repairs.filter(r => r.status === 'En attente de réception' || r.status === 'En attente de récupération');

  // ─── Render ────────────────────────────────────────────────────
  return (
    <Layout>
    <div className="space-y-6" data-testid="admin-b2b-page">
      <PageHeader
        title={t('adminB2b.title')}
        subtitle="Entreprises, réparations et paiements B2B"
        backTo="/dashboard"
        icon={Building2}
        testId="admin-b2b-header"
        actions={(
          <>
            <button type="button" onClick={sendMonthlyRecap} disabled={recapLoading}
              className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 border border-blue-500/30 text-blue-300 rounded-xl text-sm font-medium hover:bg-blue-500/30 transition-colors disabled:opacity-50"
              data-testid="admin-b2b-monthly-recap-btn">
              <Mail className="w-4 h-4" />
              {recapLoading ? t('adminB2b.sending') : t('adminB2b.monthlyRecap')}
            </button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t('adminB2b.search')} className={`${staffInputClass} pl-10 w-48 md:w-60`}
                data-testid="b2b-search" />
            </div>
          </>
        )}
      />

      {/* Monthly recap results */}
      {recapResult && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl space-y-2" data-testid="admin-b2b-recap-results">
          <div className="flex justify-between items-center">
            <h3 className="text-blue-300 font-semibold text-sm">{t('adminB2b.recapTitle')}</h3>
            <button onClick={() => setRecapResult(null)} className="text-gray-500 hover:text-white text-xs">{t('common.close')}</button>
          </div>
          {recapResult.map((r, i) => (
            <div key={i} className="flex justify-between items-center text-sm">
              <span className="text-white">{r.company} ({r.email})</span>
              <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'sent' ? 'bg-green-500/20 text-green-300' : r.status === 'skipped' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
                {r.status === 'sent' ? `Envoyé (${r.repairs_count} répar., ${r.total_amount?.toFixed(2)}€)` : r.status === 'skipped' ? 'Aucune réparation' : 'Erreur'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setActiveTab('companies')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${activeTab === 'companies' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
          data-testid="admin-b2b-tab-companies">
          <Building2 className="w-4 h-4" /> {t('adminB2b.companies')} ({companies.length})
        </button>
        <button onClick={() => setActiveTab('repairs')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 relative ${activeTab === 'repairs' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
          data-testid="admin-b2b-tab-repairs">
          <Wrench className="w-4 h-4" /> {t('adminB2b.b2bRepairs')} ({repairs.length})
          {pendingArrivals.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{pendingArrivals.length}</span>
          )}
        </button>
        <button onClick={() => setActiveTab('payments')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${activeTab === 'payments' ? 'bg-green-500/20 text-green-300 border border-green-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
          data-testid="admin-b2b-tab-payments">
          <CreditCard className="w-4 h-4" /> {t('adminB2b.paymentHistory')}
        </button>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="text-center py-10 text-gray-400">{t('common.loading')}</div>
      ) : activeTab === 'companies' ? (
        <CompaniesTab
          companies={companies} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          expandedId={expandedId} setExpandedId={setExpandedId}
          updateCompanyStatus={updateCompanyStatus} openCompanyEditModal={openCompanyEditModal}
          deleteCompany={deleteCompany} search={search} t={t} API_URL={API_URL}
        />
      ) : activeTab === 'repairs' ? (
        <RepairsTab
          repairs={repairs} repairStatusFilter={repairStatusFilter} setRepairStatusFilter={setRepairStatusFilter}
          search={search} confirmArrival={confirmArrival} acceptRefuseRepair={acceptRefuseRepair}
          updateRepairStatus={updateRepairStatus} deleteRepair={deleteRepair}
          openPriceModal={openPriceModal} openEditModal={openEditModal}
          handlePrintPdf={handlePrintPdf} handlePrintInvoice={handlePrintInvoice}
          sendDocumentEmail={sendDocumentEmail} sendingEmailRef={sendingEmailRef}
          statusModalRepair={statusModalRepair} setStatusModalRepair={setStatusModalRepair}
          priceModalRepair={priceModalRepair} setPriceModalRepair={setPriceModalRepair}
          priceForm={priceForm} setPriceForm={setPriceForm} savePrice={savePrice}
          repairInvoices={repairInvoices} markInvoicePaid={markInvoicePaid}
          printInvoiceById={printInvoiceById} finalizeBilling={finalizeBilling}
          onOpenChat={(repair) => setChatContext({ relatedType: 'b2b_repair', relatedId: repair.ref, title: `${repair.ref} - ${repair.company_name}` })}
          onOpenDetail={(repair) => setDetailRepair(repair)}
          t={t} API_URL={API_URL}
        />
      ) : activeTab === 'payments' ? (
        <PaymentsTab
          paymentHistory={paymentHistory} paymentTotals={paymentTotals} companies={companies}
          payFilterCompany={payFilterCompany} setPayFilterCompany={setPayFilterCompany}
          payFilterDateDebut={payFilterDateDebut} setPayFilterDateDebut={setPayFilterDateDebut}
          payFilterDateFin={payFilterDateFin} setPayFilterDateFin={setPayFilterDateFin}
          fetchPaymentHistory={fetchPaymentHistory} printInvoiceById={printInvoiceById} t={t}
        />
      ) : null}

      {/* Modals */}
      <EditRepairModal
        editRepair={editRepair} setEditRepair={setEditRepair}
        editForm={editForm} setEditForm={setEditForm}
        saveEdit={saveEdit} savingEdit={savingEdit}
        api={api}
      />
      <EditCompanyModal
        editModal={editModal} setEditModal={setEditModal}
        companyEditForm={companyEditForm} setCompanyEditForm={setCompanyEditForm}
        submitEditCompany={submitEditCompany} savingCompanyEdit={savingCompanyEdit}
      />

      <ChatModal
        isOpen={!!chatContext}
        onClose={() => setChatContext(null)}
        relatedType={chatContext?.relatedType}
        relatedId={chatContext?.relatedId}
        title={chatContext?.title}
      />

      <B2BRepairDetailModal
        repair={detailRepair}
        isOpen={!!detailRepair}
        onClose={() => setDetailRepair(null)}
        api={api}
        token={localStorage.getItem('token')}
        onOpenChat={(repair) => { setDetailRepair(null); setChatContext({ relatedType: 'b2b_repair', relatedId: repair.ref, title: `${repair.ref} - ${repair.company_name}` }); }}
      />
    </div>
    </Layout>
  );
};

export default AdminB2BPage;
